import {
    DeliveryState,
    useMarkDeliveriesAsAbortedMutation,
    useMarkDeliveriesAsArchivedMutation,
    useMarkDeliveriesAsDeliveredMutation,
    useMarkDeliveriesAsInProgressMutation,
    useMarkDeliveriesAsPackagingMutation,
    useMarkDeliveriesAsReadyMutation,
} from 'data/__generated__';

import { confirmPopup } from 'components/Popups/Confirm';

/**
 * Transitions proposées depuis chaque état.
 *
 * C'est une table, et non six boutons grisés au cas par cas : la fiche n'affiche que ce qui est
 * faisable maintenant. Le serveur reste seul juge — il revalide chaque passage.
 */
export const ALLOWED_TRANSITIONS: Record<DeliveryState, DeliveryState[]> = {
    [DeliveryState.Created]: [DeliveryState.Packaging, DeliveryState.Aborted],
    [DeliveryState.Packaging]: [DeliveryState.Ready, DeliveryState.Delivered, DeliveryState.Aborted],
    [DeliveryState.Ready]: [DeliveryState.InProgress, DeliveryState.Delivered, DeliveryState.Aborted],
    [DeliveryState.InProgress]: [DeliveryState.Delivered, DeliveryState.Aborted],
    [DeliveryState.NotDelivered]: [DeliveryState.Delivered, DeliveryState.Aborted, DeliveryState.Archived],
    [DeliveryState.Delivered]: [DeliveryState.Archived],
    [DeliveryState.Aborted]: [DeliveryState.Archived],
    [DeliveryState.Archived]: [],
    [DeliveryState.Paused]: [],
};

/**
 * Libellés d'action — volontairement différents des libellés d'état (`DELIVERY_STATES_LABELS`) :
 * ce sont ceux que les opés connaissent, et ceux que les specs E2E cliquent.
 */
export const TRANSITION_LABELS: Partial<Record<DeliveryState, string>> = {
    [DeliveryState.Packaging]: 'Passer en préparation',
    [DeliveryState.Ready]: 'Marquer comme prête',
    [DeliveryState.InProgress]: 'Passer en livraison',
    [DeliveryState.Delivered]: 'Marquer comme livrée',
    [DeliveryState.Aborted]: 'Annuler la livraison',
    [DeliveryState.Archived]: 'Archiver la livraison',
};

export type DeliveryStateAction = {
    state: DeliveryState;
    label: string;
    run: () => Promise<void>;
};

export function useDeliveryStateActions({
    deliveryId,
    state,
    hasUnsavedChanges,
}: {
    deliveryId: string;
    state: DeliveryState;
    /** Un changement d'état relit la livraison : les quantités préparées saisies seraient écrasées. */
    hasUnsavedChanges: boolean;
}): { actions: DeliveryStateAction[]; loading: boolean } {
    // Ces mutations ne renvoient qu'un `DeliveryFragment`, plus maigre que la sélection de
    // GET_DELIVERY (le rangement créé au passage en livrée n'y arrive par exemple qu'avec son `_id`).
    // Sans re-fetch, la query de la page reste incomplète : Apollo continue de servir le dernier
    // résultat complet et les transitions suivantes ne s'ouvrent pas.
    const stateMutationOptions = {
        variables: { deliveryIds: [deliveryId] },
        refetchQueries: ['GET_DELIVERY'],
        awaitRefetchQueries: true,
    };

    const [markAsAborted, { loading: abortedLoading }] = useMarkDeliveriesAsAbortedMutation(stateMutationOptions);
    const [markAsArchived, { loading: archivedLoading }] = useMarkDeliveriesAsArchivedMutation(stateMutationOptions);
    const [markAsDelivered, { loading: deliveredLoading }] = useMarkDeliveriesAsDeliveredMutation(stateMutationOptions);
    const [markAsInProgress, { loading: inProgressLoading }] =
        useMarkDeliveriesAsInProgressMutation(stateMutationOptions);
    const [markAsPackaging, { loading: packagingLoading }] = useMarkDeliveriesAsPackagingMutation(stateMutationOptions);
    const [markAsReady, { loading: readyLoading }] = useMarkDeliveriesAsReadyMutation(stateMutationOptions);

    const loading =
        abortedLoading || archivedLoading || deliveredLoading || inProgressLoading || packagingLoading || readyLoading;

    const runners: Partial<Record<DeliveryState, () => Promise<unknown>>> = {
        [DeliveryState.Packaging]: markAsPackaging,
        [DeliveryState.Ready]: markAsReady,
        [DeliveryState.InProgress]: markAsInProgress,
        [DeliveryState.Delivered]: markAsDelivered,
        [DeliveryState.Aborted]: markAsAborted,
        [DeliveryState.Archived]: markAsArchived,
    };

    const actions = ALLOWED_TRANSITIONS[state].flatMap((targetState) => {
        const label = TRANSITION_LABELS[targetState];
        const run = runners[targetState];

        if (!label || !run) {
            return [];
        }

        return [
            {
                state: targetState,
                label,
                run: async () => {
                    const hasConfirmed = await confirmPopup({
                        title: 'Confirmer',
                        message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?${
                            hasUnsavedChanges
                                ? '\n\nLa livraison sera rechargée : vos modifications non enregistrées seront perdues.'
                                : ''
                        }`,
                    });

                    if (hasConfirmed) {
                        await run();
                    }
                },
            },
        ];
    });

    return { actions, loading };
}
